/**
 * Контролы экранов: сегменты, период, сортируемый заголовок, названия движков.
 *
 * Правила — app-kit.md §2 и §3. Из них два не обсуждаются: подпись у поля видна
 * всегда, а сортируемый заголовок — настоящая кнопка внутри `th` с `aria-sort`
 * на самом `th`. Числовые колонки выровнены вправо: `seeno.css` выравнивает
 * `td.tnum` влево, и это противоречит правилу табличных цифр (app-kit.md §3).
 */

import type { KeyboardEvent, ReactNode } from 'react';

import { t } from '../i18n';
import type { components } from '../api/schema';
import { engineName } from '@shared/src/product';

type Engine = components['schemas']['Engine'];

/* ───────────────────────────── переключатель ──────────────────────────────── */

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Сегменты как настоящий tablist: стрелки двигают внутри, Tab выводит наружу.
 */
export function Segments<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = options.findIndex((option) => option.value === value);
    if (index < 0) return;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % options.length;
    else if (event.key === 'ArrowLeft')
      next = (index - 1 + options.length) % options.length;
    else return;
    event.preventDefault();
    const option = options[next];
    if (option) onChange(option.value);
  };

  return (
    <div
      className="seg light"
      role="tablist"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={move}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          tabIndex={option.value === value ? 0 : -1}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ──────────────────────────────── период ─────────────────────────────────── */

export type PeriodKey = '7' | '30' | '90';

/**
 * Варианты периода. Функция, а не таблица: подписи берутся из каталога в момент
 * отрисовки, а таблица посчиталась бы один раз, до того как известна локаль
 * вошедшего (B5).
 */
function periods(): readonly SegmentOption<PeriodKey>[] {
  return [
    { value: '7', label: t('control.period.week') },
    { value: '30', label: t('control.period.month') },
    { value: '90', label: t('control.period.quarter') },
  ];
}

export function PeriodPicker({
  value,
  onChange,
}: {
  value: PeriodKey;
  onChange: (value: PeriodKey) => void;
}) {
  return (
    <Segments
      label={t('control.period')}
      options={periods()}
      value={value}
      onChange={onChange}
    />
  );
}

/* ──────────────────────── сортируемый заголовок ──────────────────────────── */

export type SortDirection = 'asc' | 'desc';

export function SortTh<K extends string>({
  column,
  sort,
  onSort,
  numeric = false,
  title,
  children,
}: {
  column: K;
  sort: { key: K; direction: SortDirection };
  onSort: (key: K) => void;
  numeric?: boolean;
  /** Определение показателя. Каждая колонка-метрика обязана его нести. */
  title?: string;
  children: ReactNode;
}) {
  const active = sort.key === column;
  return (
    <th
      scope="col"
      className={`sortable${numeric ? ' num' : ''}`}
      aria-sort={
        active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      title={title}
    >
      <button type="button" onClick={() => onSort(column)}>
        {children}
        {active ? (
          <i
            className={`sort-mark${sort.direction === 'desc' ? ' desc' : ''}`}
            aria-hidden="true"
          />
        ) : null}
      </button>
    </th>
  );
}

/* ──────────────────────────── названия движков ───────────────────────────── */

/**
 * Код движка в то, что видит клиент. Название приходит из справочника
 * `GET /engines` — второй список названий в интерфейсе разъедется с ним на
 * первом же новом движке. Порядок тоже справочника: перечислять движки в
 * произвольном порядке значит каждый раз читать таблицу заново.
 */
export function engineTitles(engines: Engine[] | undefined): Map<string, string> {
  return new Map((engines ?? []).map((engine) => [engine.code, engine.title]));
}

export function engineTitle(titles: Map<string, string>, code: string): string {
  return engineName(code, titles.get(code));
}

export function byEngineOrder(engines: Engine[] | undefined) {
  const order = new Map(
    (engines ?? []).map((engine, i) => [engine.code, engine.sort_order ?? i]),
  );
  return (a: string, b: string) => (order.get(a) ?? 99) - (order.get(b) ?? 99);
}
